<template>
  <div class="container mt-5">
    <h1>AI Configuration Interface</h1>
    <div class="row">
      <div class="col-md-4">
        <div class="row col-12 mb-3">
          <product-selector
            :products="products"
            v-model="selectedProduct"
            @product-changed="handleProductChange"
          />
        </div>
        <div class="row col-12 mb-3">
          <StyleSelector
            :options="motores"
            name="motores"
            v-model="selectedMotor"
            @update:modelValue="updateMotor"
          />
          <p>Selected motor: {{ selectedMotor }}</p>
        </div>
        <div class="row col-12 mb-3">
          <ModulatorControl
            v-for="(modulador, key) in moduladores"
            :key="key"
            :name="key"
            :modulador="modulador"
            @update:value="updateModulator"
          />
        </div>
        <div class="row col-12 mb-3">
          <h4>Config Object for {{ selectedProduct }}:</h4>
          <div class="input-group mb-3">
            <span class="input-group-text" id="basic-addon1">Nombre</span>
            <input
              type="text"
              class="form-control"
              placeholder="Nombre de la configuración"
              aria-label="Config"
              aria-describedby="basic-addon1"
              v-model="configName"
              @keyup="handleConfigNameKeyUp"
            />
          </div>
          <pre>{{ prettyPrintedJSON }}</pre>
        </div>
        <div class="row col-12 mb-3">
          <action-button
            @reset-config="resetConfig"
            @save-config="saveConfig"
          />
        </div>
      </div>
      <div class="col-md-8">
        <chat-window :messages="messages" ref="chatWindow" />
        <chat-input @send-message="handleSendMessage" @clear-chat="cleanChatWidnow" />
      </div>
    </div>
    <FloatingAlert :show="showAlert" :alertType="alertType" :message="alertMessage" />
  </div>
</template>

<script setup>
import ProductSelector from './components/ProductSelector.vue'
import StyleSelector from './components/StyleSelector.vue'
import ModulatorControl from './components/ModulatorControl.vue'
import ActionButton from './components/ActionButton.vue'
import ChatWindow from '@common/widgets/ChatWindow.vue'
import ChatInput from '@common/widgets/ChatInput.vue'
import FloatingAlert from '@common/widgets/FloatingAlert.vue'
import { ApiConsumer } from '@common/api/ApiConsumer.js'
import { checkOrCreateUniqid } from '@common/utils/cookies.js'
import { ref, computed, onMounted, reactive } from 'vue';
import config from './config';

//usemos localStorage para guardar las sesiones, mensajes y la sesión seleccionada
const messages = ref(JSON.parse(localStorage.getItem('messages')) || []);
const session_id = ref(localStorage.getItem('session_id') || checkOrCreateUniqid());
const apiConsumer = new ApiConsumer();
const apiBaseUrl = config.apiBaseUrl;
const messageEndpoint = `${apiBaseUrl}/chat/ask`;
const configEndpoint = `${apiBaseUrl}/product/config`;

const initialState = {
  motor: 'gpt-3.5-turbo',
  tokens: 50,
  tecnico: 50,
  casual: 50,
};

const showAlert = ref(false);
const alertType = ref('success');
const alertMessage = ref('');

const appState = reactive({
  product: {
    config: {
      configErrorCode: ref(null),
      configMessage: ref(''),
      configName: ref(''),
    },
  },
});

const configName = ref('');
const products = ref([
  {id:'product1', name:'Producto 1'},
  {id:'product2', name:'Producto 2'},
]);
const selectedProduct = ref('product1');
const motores = ref([
  { id: "gpt-3.5-turbo", name: 'ChatGPT 3.5 turbo' },
  { id: "gpt-4", name: 'ChatGPT 4' },
]);
const selectedMotor = ref(initialState.motor);
const moduladores = ref({
  tokens: ref({ value: initialState.tokens, min: 20, max: 200 }),
  tecnico: ref({ value: initialState.tecnico, mix: 0, max: 100 }),
  casual: ref({ value: initialState.casual, mix: 0, max: 100 }),
});
const configObject = ref(initialState);

// Initialize the session_id cookie
const handleSendMessage = async (message) => {
  try {
    const messageObj = { id: Date.now(), text: message, class: ['message', 'question', 'sent'] };
    const response = await apiConsumer.post(messageEndpoint, { pregunta: message, session_id: session_id.value });
    if (response.status === 200) {
      const answerObj = { id: Date.now(), text: response.data.answer, class: ['message', 'answer', 'received'] };
      appendMessage(messageObj);
      appendMessage(answerObj);
    } else {
      console.error('Error sending message:', response.error);
    }
  } catch (error) {
    console.error('Error handling:', error);
  }
};

const appendMessage = (message) => {
  messages.value.push(message);
};

const cleanChatWidnow = () => {
  messages.value = [];
};

const handleProductChange = (productId) => {
  console.log('Product changed to:', productId);
};

const updateModulator = ({ name, value }) => {
  moduladores.value[name].value = value;
  handleConfigObject();
};

const updateMotor = () => {
  handleConfigObject();
};

const handleConfigNameKeyUp = () => {
  configObject.value.name = configName.value;
  handleConfigObject();
};

const handleConfigObject = () => {
  configObject.value = {
    name: configName.value,
    motor: selectedMotor.value,
    tokens: moduladores.value.tokens.value,
    tecnico: moduladores.value.tecnico.value,
    casual: moduladores.value.casual.value,
  };
};

const prettyPrintedJSON = computed(() => {
  return JSON.stringify(configObject.value, null, 2);
});

const resetConfig = () => {
  configName.value = '';
  selectedMotor.value = initialState.motor;
  Object.keys(moduladores.value).forEach((key) => {
    moduladores.value[key].value = initialState[key];
  });
  handleConfigObject();
};

const saveConfig = async () => {
  if (configName.value === '') {
    console.error('Config name cannot be empty');
    appState.product.config.configErrorCode = 1;
    appState.product.config.configMessage = 'Config name cannot be empty';
    return;
  }
  try {
    const response = await apiConsumer.post(configEndpoint, {
      product: selectedProduct.value,
      config: configObject.value,
    });
    
    showAlert.value = true;
    alertMessage.value = response.msg;
  } catch (error) {
    showAlert.value = true;
    alertType.value = 'danger';
    alertMessage.value = error.msg;
  }
};

onMounted(() => {
  handleConfigObject();
});
</script>

<style>
.container {
  margin-top: 20px;
}
</style>
